import apiClient from '@/api/client';
import { getActiveHospitalId } from '@/constants';

const transformPatient = (p) => {
  if (!p) return null;
  const nameParts = (p.name || '').split(' ');
  return {
    ...p,
    firstName: p.firstName || nameParts[0] || '',
    lastName: p.lastName || nameParts.slice(1).join(' ') || '',
    phone: p.phone || p.contactNumber || '',
    contactNumber: p.contactNumber || p.phone || '',
    dateOfBirth: p.dateOfBirth || p.dob || '',
    status: p.isActive ? 'ACTIVE' : (p.status || 'INACTIVE'),
    gender: p.gender ? p.gender.toLowerCase() : '',
  };
};

export const patientService = {
  /**
   * GET /pms/hospital/{hospitalId}/patients
   */
  getAll: async (params = {}, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const res = await apiClient.get(`/hospital/${activeHospId}/patients`, {
      params: {
        page: (params.page || 1) - 1,
        size: params.limit || 20,
        search: params.search || undefined,
        gender: params.gender !== 'all' ? params.gender?.toUpperCase() : undefined,
        bloodGroup: params.bloodGroup !== 'all' ? params.bloodGroup : undefined,
      },
    });

    const items = (res.content || res.data || []).map(transformPatient);
    return {
      data: items,
      total: res.totalElements !== undefined ? res.totalElements : items.length,
      page: (res.pageNumber !== undefined ? res.pageNumber : 0) + 1,
      totalPages: res.totalPages || 1,
    };
  },

  /**
   * GET /pms/hospital/{hospitalId}/patients/{patientId}
   */
  getById: async (id, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const res = await apiClient.get(`/hospital/${activeHospId}/patients/${id}`);
    return transformPatient(res.data || res);
  },

  /**
   * POST /pms/hospital/{hospitalId}/patients
   */
  create: async (data, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const fullName = `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.name;
    const cleanPhone = (p) => {
      if (!p) return undefined;
      const clean = String(p).replace(/\s+/g, '');
      return /^[+]?[0-9]{7,15}$/.test(clean) ? clean : undefined;
    };

    const payload = {
      name: fullName,
      age: data.age !== undefined && data.age !== '' ? parseInt(data.age, 10) : undefined,
      gender: (data.gender || 'OTHER').toUpperCase(),
      dob: data.dateOfBirth || data.dob || undefined,
      contactNumber: cleanPhone(data.phone || data.contactNumber),
      email: data.email?.trim() || undefined,
      address: typeof data.address === 'object'
        ? `${data.address.street || ''}, ${data.address.city || ''}, ${data.address.state || ''} ${data.address.pinCode || ''}`.trim() || undefined
        : data.address || undefined,
      bloodGroup: data.bloodGroup || undefined,
      emergencyContact: cleanPhone(typeof data.emergencyContact === 'object' ? data.emergencyContact.phone : data.emergencyContact),
      medicalHistory: Array.isArray(data.chronicConditions)
        ? data.chronicConditions.join(', ')
        : data.medicalHistory,
    };

    const res = await apiClient.post(`/hospital/${activeHospId}/patients`, payload);
    return transformPatient(res.data || res);
  },

  /**
   * PUT /pms/hospital/{hospitalId}/patients/{patientId}
   */
  update: async (id, data, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const fullName = `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.name;
    const cleanPhone = (p) => {
      if (!p) return undefined;
      const clean = String(p).replace(/\s+/g, '');
      return /^[+]?[0-9]{7,15}$/.test(clean) ? clean : undefined;
    };

    const payload = {
      name: fullName,
      age: data.age !== undefined && data.age !== '' ? parseInt(data.age, 10) : undefined,
      gender: (data.gender || 'OTHER').toUpperCase(),
      dob: data.dateOfBirth || data.dob || undefined,
      contactNumber: cleanPhone(data.phone || data.contactNumber),
      email: data.email?.trim() || undefined,
      address: typeof data.address === 'object'
        ? `${data.address.street || ''}, ${data.address.city || ''}, ${data.address.state || ''} ${data.address.pinCode || ''}`.trim() || undefined
        : data.address || undefined,
      bloodGroup: data.bloodGroup || undefined,
      emergencyContact: cleanPhone(typeof data.emergencyContact === 'object' ? data.emergencyContact.phone : data.emergencyContact),
      medicalHistory: Array.isArray(data.chronicConditions)
        ? data.chronicConditions.join(', ')
        : data.medicalHistory,
    };

    const res = await apiClient.put(`/hospital/${activeHospId}/patients/${id}`, payload);
    return transformPatient(res.data || res);
  },

  /**
   * GET /pms/hospital/{hospitalId}/patients/{patientId}/documents
   */
  getDocuments: async (patientId, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    try {
      const res = await apiClient.get(`/hospital/${activeHospId}/patients/${patientId}/documents`);
      const items = Array.isArray(res) ? res : (res?.data || res?.content || []);
      return { data: items };
    } catch (err) {
      return { data: [] };
    }
  },

  /**
   * POST /pms/hospital/{hospitalId}/patients/{patientId}/documents
   */
  uploadDocument: async (patientId, fileData, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const payload = {
      name: fileData.name,
      type: fileData.type || 'other',
      fileType: fileData.fileType || 'pdf',
      size: fileData.size || 0,
      url: fileData.url || `https://storage.pulseflow.in/docs/${fileData.name}`,
    };

    const res = await apiClient.post(`/hospital/${activeHospId}/patients/${patientId}/documents`, payload);
    return res.data || res;
  },

  /**
   * DELETE /pms/hospital/{hospitalId}/patients/{patientId}/documents/{docId}
   */
  deleteDocument: async (patientId, docId, hospitalId) => {
    const activeHospId = getActiveHospitalId(hospitalId);
    const targetPatientId = patientId || 'default';
    const res = await apiClient.delete(`/hospital/${activeHospId}/patients/${targetPatientId}/documents/${docId}`);
    return res.data || res;
  },
};

export default patientService;
